import { DefaultRoutes } from 'Routes';
import { useCallback } from 'react';
import { useReportId } from 'hooks/helperHooks';
import { useReportQueries } from 'utilities/reportQueriesFactory';
import useReportStore from './reportStore';
import { useStores } from 'index';
import { useReportLayoutManagement } from 'hooks/useReportLayoutManagement';
import { useShallow } from 'zustand/react/shallow';

// This hook connects queries and store with the rest of the app. This is the only hook that should be used to access data and queries.

const usePitcherSingleSessionReport = () => {
  const codegenReportService = useStores().codegenReportService;
  const { isCreate, reportId } = useReportId();

  const actions = useReportStore((state) => state.actions);

  const data = useReportStore(
    useShallow((state) => ({
      isEditMode: state.isEditMode,
      searchCriteria: state.searchCriteria,
      searchChips: state.searchChips,
      specialConfigOptions: state.specialConfigOptions,
      selectedConfgValues: state.selectedConfgValues,
      layout: state.layout,
      chartData: state.chartData,
      tempLayout: state.tempLayout,
      editingChart: state.editingChart,
    }))
  );

  const { isLoadingReport, saveOrgLayout, generateReportMutation, updateReportSetupMutation } = useReportQueries({
    reportRoute: DefaultRoutes.SessionReviewReport.PitcherSingleSession,
    useReportStore: useReportStore,
    getOrgLayoutFn: codegenReportService.getReportSessionReviewReportPitcherSingleSessionOrgLayout,
    saveOrgLayoutFn: codegenReportService.postReportSessionReviewReportPitcherSingleSessionOrgLayout,
    generateReportFn: codegenReportService.postReportSessionReviewReportPitcherSingleSessionGenerate,
    getReportDataFn: codegenReportService.postReportSessionReviewReportPitcherSingleSessionGetReportData,
    updateReportSetupFn: codegenReportService.postReportSessionReviewReportPitcherSingleSessionUpdateReportSetup,
    setReportMetadata: (reportData) => {
      const { pitchTypeInfo, chartData: pageData, ...rest } = reportData;
      return {
        newSearchChips: {
          seasonNames: rest.seasonNames,
          dateRanges: rest.dateRanges,
          levelName: rest.levelName,
          teamName: rest.teamName,
          playerNames: Array.from(new Set(pageData.map((item) => item.playerName))),
          sessionNames: Array.from(new Set(pageData.map((item) => item.sessionName))),
        },
        newSpecialConfigOptions: { pitchTypeInfo },
      };
    },
  });

  const { hasUnsavedEdits, discardLayoutChanges, saveLayoutForSession, saveLayoutForOrg } = useReportLayoutManagement({
    isEditMode: data.isEditMode,
    tempLayout: data.tempLayout,
    layout: data.layout,
    setTempLayout: actions.setTempLayout,
    setLayout: actions.setLayout,
    reportId,
    isCreate,
    selectedConfgValues: data.selectedConfgValues,
    updateReportSetupMutation,
    saveOrgLayout,
  });

  const getNewData = useCallback(() => {
    actions.setCanGetNewData(true);
  }, [actions]);

  const generateReport = useCallback(() => {
    actions.setCanGetNewData(false);
    const { gameType, dateRanges, seasonNames, level, team, players, sessions } = data.searchCriteria;

    if (data.layout && gameType && dateRanges && level && team && players && sessions) {
      generateReportMutation({
        requestBody: {
          gameType,
          dateRanges,
          seasonNames: seasonNames ?? [],
          level,
          team,
          players,
          sessions,
          reportContentConfig: data.selectedConfgValues,
          configData: data.layout,
        },
      });
    }
  }, [data.searchCriteria, data.layout, data.selectedConfgValues, actions, generateReportMutation]);

  const setSearchCriteria = useCallback(
    (criteria: Partial<typeof data.searchCriteria>) => {
      actions.setPartialSearchCriteria(criteria);
      actions.setPartialSearchChips({
        ...('dateRanges' in criteria && { dateRanges: criteria.dateRanges }),
        ...('seasonNames' in criteria && { seasonNames: criteria.seasonNames }),
        ...('level' in criteria && { levelName: criteria.level?.name }),
        ...('team' in criteria && { teamName: criteria.team?.name }),
        ...('players' in criteria && { playerNames: criteria.players?.map((p) => p.name) }),
        ...('sessions' in criteria && { sessionNames: criteria.sessions?.map((s) => s.name) }),
      });
    },
    [actions]
  );

  return {
    ...data,
    ...actions,
    setSearchCriteria,
    isLoadingReport,
    generateReport,
    getNewData,
    hasUnsavedEdits,
    discardLayoutChanges,
    saveLayoutForSession,
    saveLayoutForOrg,
  };
};

export default usePitcherSingleSessionReport;
